import React from 'react';
import { cn } from '@rocco/utils/cn';
import type { UserActiveStatus } from '~/modules/user';
import { ParticipantAvatar } from './ParticipantAvatar';
import { StatusIndicator } from './StatusIndicator';

type ParticipantAvatarProps = React.ComponentProps<typeof ParticipantAvatar>;

/**
 * The avatar component for a group chat. This is a circle with the
 * participants' avatars inside, arranged in a circle.
 */
export function GroupChatAvatar({
    className,
    avatarClassName,
    imageClassName,
    fallbackClassName,
    participants,
    activeStatus,
    maxAvatars = 7,
}: {
    className?: string;
    avatarClassName?: string;
    imageClassName?: string;
    fallbackClassName?: string;
    participants: NonNullable<ParticipantAvatarProps['participant']>[];
    activeStatus?: UserActiveStatus;
    maxAvatars?: number;
}) {
    const slicedParticipants = participants.slice(
        0,
        participants.length <= maxAvatars ? maxAvatars : maxAvatars - 1
    );
    const extraParticipants = Math.max(
        participants.length - slicedParticipants.length,
        0
    );
    const avatarCount = slicedParticipants.length + (extraParticipants ? 1 : 0);
    const transform =
        'translate(-50%, -50%) rotate(var(--avatar-angle)) translateY(-25%) rotate(calc(-1 * var(--avatar-angle))) scale(var(--avatar-scale))';

    return maxAvatars <= 1 || participants.length <= 1 ? (
        <ParticipantAvatar
            className={avatarClassName}
            imageClassName={imageClassName}
            fallbackClassName={fallbackClassName}
            participant={participants[0]}
            activeStatus={activeStatus}
        />
    ) : (
        <div
            className={cn('relative size-10 rounded-full bg-white', className)}
        >
            {slicedParticipants.map((participant, index) => (
                <ParticipantAvatar
                    key={participant.ID}
                    className={cn(
                        'absolute left-1/2 top-1/2 size-10',
                        avatarClassName
                    )}
                    style={
                        {
                            '--avatar-angle': `${calculateAvatarAngle(index, avatarCount)}deg`,
                            '--avatar-scale': `${calculateAvatarScale(avatarCount)}`,
                            transform,
                        } as React.CSSProperties
                    }
                    imageClassName={imageClassName}
                    fallbackClassName={cn(
                        'bg-black/60 backdrop-blur-sm',
                        fallbackClassName
                    )}
                    participant={participant}
                    singleLetterFallback
                />
            ))}
            {extraParticipants > 0 && (
                <div
                    className={cn(
                        'absolute left-1/2 top-1/2 z-1 flex size-10 items-center justify-center rounded-full bg-black/60 backdrop-blur-sm',
                        avatarClassName
                    )}
                    style={
                        {
                            '--avatar-angle': `${calculateAvatarAngle(avatarCount - 1, avatarCount)}deg`,
                            '--avatar-scale': `${calculateAvatarScale(avatarCount)}`,
                            transform,
                        } as React.CSSProperties
                    }
                >
                    <span
                        className={cn(
                            'whitespace-nowrap text-5 leading-tight text-white',
                            fallbackClassName
                        )}
                    >
                        +{extraParticipants}
                    </span>
                </div>
            )}
            {activeStatus && (
                <StatusIndicator className="z-1" status={activeStatus} />
            )}
        </div>
    );
}

/**
 * Calculate the angle in degrees for positioning an avatar in a circle.
 * @param index The zero-based index of the avatar.
 * @param totalCount The total number of avatars to arrange in the circle
 * @returns The angle in degrees.
 */
function calculateAvatarAngle(index: number, totalCount: number): number {
    let angle = (index / totalCount) * 360;
    if (totalCount <= 2) {
        // Start at left side if only 2 avatars.
        angle -= 90;
    }
    return angle;
}

/**
 * Calculate the scale factor for an avatar in a circle.
 * @param totalCount The total number of avatars to arrange in the circle
 * @returns The scale factor.
 */
function calculateAvatarScale(totalCount: number): number {
    // Ranges from 0.5 to 0.35-, from 2 to 7+ participants.
    return 0.5 - 0.15 * ((totalCount - 2) / 5);
}
